import { Button } from "antd"
import { Loader2, PhoneCall } from "lucide-react"
import { useState } from "react"

import { fetchDialerPhoneNumber } from "~/api/marketing-os"
import CustomTooltip from "~/components/custom-tooltip"
import useGlobalStore from "~/store/global"
import useVoiceSessionStore, { useCallBlocked } from "~/store/voice-session"
import type { ITokenInfo } from "~/types/global"

export type CallNowPerson = {
  id?: string | number | null
  name?: string | null
  linkedin_url?: string | null
  job_title?: string | null
  account_name?: string | null
  location?: string | null
}

// people_os only. VoiceDialerHost also mounts for
// unified_os_key_actions_enabled, so this is deliberately narrower than the
// host: both tenants carry that flag by default, which would put the button in
// front of everyone rather than the people the dialer is being rolled out to.
export function useCallNowEnabled() {
  const user = useGlobalStore((store) => store.user)
  return !!user?.feature_flags?.people_os
}

// show_get_phone alone. The two affordances are independent: a tenant can carry
// the dialer, the phone lookup, both or neither, and each button answers only to
// its own flag. Coupling them meant turning the dialer on silently removed Get
// phone from people who still had it.
export function useShowGetPhone() {
  const user = useGlobalStore((store) => store.user)
  return !!user?.feature_flags?.show_get_phone
}

// Opens the real dialer, not a modal that collects a number and hands it on.
// ad-hoc-call.ts already learned that lesson: a bespoke "Call a number" dialog
// meant two surfaces asking for a phone number and only one of them dialling.
// A number we can enrich is found first, so the panel opens on compose with the
// number already on file and a Call button, rather than an empty field.
export default function CallNowButton({
  phone,
  person,
  resolvePhone,
  size = "small",
  className
}: {
  phone?: string | null
  person?: CallNowPerson | null
  resolvePhone?: () => Promise<string | null | undefined>
  size?: "small" | "middle" | "large"
  className?: string
}) {
  const enabled = useCallNowEnabled()
  const sessionActive = useCallBlocked()
  const startSingle = useVoiceSessionStore((s) => s.startSingle)
  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const [resolving, setResolving] = useState(false)

  if (!enabled) return null

  const lookup = async (): Promise<string | null> => {
    if (resolvePhone) return (await resolvePhone()) ?? null
    if (!tokenInfo || !person?.linkedin_url) return null
    try {
      const res = await fetchDialerPhoneNumber(
        tokenInfo,
        [
          {
            name: person.name || "",
            linkedin_url: person.linkedin_url,
            seniority: person.job_title || "",
            account: person.account_name || "",
            location: person.location || ""
          }
        ] as never,
        true,
        true
      )
      const active = (res.data?.data?.[0]?.phones || []).filter(
        (p: { is_active: boolean; phone: string }) =>
          p.is_active && p.phone && p.phone !== "NA"
      )
      return active[0]?.phone ?? null
    } catch {
      return null
    }
  }

  const onClick = async () => {
    let number = phone ?? null
    if (!number) {
      setResolving(true)
      try {
        number = await lookup()
      } finally {
        setResolving(false)
      }
    }
    startSingle({
      id: String(person?.id ?? "ad-hoc"),
      name: person?.name || "Ad-hoc call",
      title: person?.job_title || "Manual dial",
      phone: number || "",
      linkedin_url: person?.linkedin_url || undefined,
      ...(number ? {} : { phone_lookup: "not_found" as const })
    })
  }

  return (
    <CustomTooltip
      title={
        <p className="text-xs">
          {sessionActive ? "Finish the call you are on first" : "Call now"}
        </p>
      }
    >
      <Button
        size={size}
        shape="circle"
        icon={
          resolving ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <PhoneCall size={14} />
          )
        }
        disabled={sessionActive || resolving}
        aria-label="Call now"
        className={className}
        onClick={(e) => {
          e.stopPropagation()
          void onClick()
        }}
      />
    </CustomTooltip>
  )
}
